import { useQuery } from '@apollo/client';
import { IonSkeletonText } from '@ionic/react';
import { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import Segment from '@components/buttons/Segment';
import CarePlan from '@components/carePlan/CarePlan';
import styles from '@components/carePlan/CarePlan.module.css';
import ExpertCarePlan from '@components/carePlan/ExpertCarePlan';
import HandleQuery from '@components/HandleQuery';
import loaderStyles from '@components/loaders/LoaderStyles.module.css';
import ExpertJourneyPage from '@components/pageTypes/ExpertJourneyPage';
import Heading2 from '@components/typography/Heading2';
import useSubscribeToJourney from '@hooks/subscriptions/useSubscribeToJourney';
import useMutationWithValidation from '@hooks/useMutationWithValidation';
import useScrollPosition from '@hooks/useScrollPosition';
import { ScrollPositionPageTypeEnum } from '@typing/Enums';
import {
  AppEventActionKindEnum,
  AppEventCreateDocument,
  AppEventCreateMutation,
  AppEventCreateMutationVariables,
  GetJourneyWithPlanDocument,
  GetJourneyWithPlanQuery,
  GetJourneyWithPlanQueryVariables,
  GetPinsDocument,
  GetPinsQuery,
  GetPinsQueryVariables
} from '@typing/Generated';
import { getCareProfileName } from '@utils/modelUtils';

type Props = {
  journeyId: string;
};

const ExpertJourneyCarePlanPage = ({ journeyId }: Props) => {
  const getJourneyWithPlanQuery = useQuery<GetJourneyWithPlanQuery, GetJourneyWithPlanQueryVariables>(
    GetJourneyWithPlanDocument,
    {
      variables: {
        id: journeyId
      }
    }
  );

  const getPinsQuery = useQuery<GetPinsQuery, GetPinsQueryVariables>(GetPinsDocument, {
    variables: {
      hideEditModePins: true,
      journeyId
    }
  });

  useSubscribeToJourney(journeyId, () => {
    getJourneyWithPlanQuery.refetch();
    getPinsQuery.refetch();
  });

  const [createAppEvent] = useMutationWithValidation<AppEventCreateMutation, AppEventCreateMutationVariables>(
    AppEventCreateDocument,
    'appEvent'
  );

  useScrollPosition(ScrollPositionPageTypeEnum.CARE_PLAN);

  useEffect(() => {
    createAppEvent({
      actionKind: AppEventActionKindEnum.care_plan_opened,
      journeyId
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const intl = useIntl();

  const buttons = [
    {
      maxWidth: 120,
      textKey: 'pages.carePlan.base.memberView',
      value: 'false'
    },
    {
      maxWidth: 120,
      textKey: 'pages.carePlan.base.expertView',
      value: 'true'
    }
  ];

  const [expertView, setExpertView] = useState(true);

  return (
    <ExpertJourneyPage helmetKey="pageTitles.expertJourney.carePlan.show" journeyId={journeyId}>
      <div className={styles.expertHeader}>
        {getJourneyWithPlanQuery.loading ? (
          <IonSkeletonText animated className={loaderStyles.skeletonH1} />
        ) : (
          <Heading2>
            <FormattedMessage
              id="pages.carePlan.base.carePlanFor"
              values={{ name: getCareProfileName({ intl, journey: getJourneyWithPlanQuery.data?.journey }) }}
            />
          </Heading2>
        )}

        <Segment
          buttons={buttons}
          onChange={v => {
            setExpertView(JSON.parse(v));
          }}
          value={expertView.toString()}
        />
      </div>
      <HandleQuery query={getJourneyWithPlanQuery}>
        {journeyData => (
          <HandleQuery query={getPinsQuery}>
            {pinsData => (
              <>
                {expertView ? (
                  <ExpertCarePlan journey={journeyData.journey} />
                ) : (
                  <CarePlan journey={journeyData.journey} pins={pinsData.pins} />
                )}
              </>
            )}
          </HandleQuery>
        )}
      </HandleQuery>
    </ExpertJourneyPage>
  );
};

export default ExpertJourneyCarePlanPage;
